import { combineReducers } from "redux";
import { configureStore, getDefaultMiddleware } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";
import storage from "redux-persist/lib/storage";
import hardSet from "redux-persist/lib/stateReconciler/hardSet";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";

import checkoutReducer from "./slices/checkout";
import serverContextReducer from "./slices/serverContext";
import plansReducer from "./slices/plans";
import recommendedPlanReducer from "./slices/recommendedPlan";
import surveyAnswersReducer from "./slices/surveyAnswers";
import trialReducer from "./slices/trial";
import surveyNameSpaceReducer from "./slices/surveyNameSpace";
import languageReducer from "./slices/language";
import userDataReducer from "./slices/userData";
import koreanProgramReducer from "./slices/koreanProgram";
import visitorStatusReducer from "./slices/visitorStatus";
import paymentEnrollmentFormReducer from "./slices/paymentEnrollmentForm";
import deviceInformationReducer from "./slices/deviceInformation";
import geoLocationReducer from "./slices/geoLocation";
import routeIdReducer from "./slices/routeId";
import startEventsReducer from "./slices/startEvents";
import customPlansReducer from "./slices/customPlans";
import linearBuyflowReducer from "./slices/linearBuyflow";

// Inlined this vs. having in a separate file to avoid a circular dependency
export const rootReducer = combineReducers({
  serverContext: serverContextReducer,
  checkout: checkoutReducer,
  plans: plansReducer,
  recommendedPlan: recommendedPlanReducer,
  surveyAnswers: surveyAnswersReducer,
  trial: trialReducer,
  userData: userDataReducer,
  surveyNameSpace: surveyNameSpaceReducer,
  language: languageReducer,
  koreanProgram: koreanProgramReducer,
  visitorStatus: visitorStatusReducer,
  paymentEnrollmentForm: paymentEnrollmentFormReducer,
  deviceInformation: deviceInformationReducer,
  geoLocation: geoLocationReducer,
  routeId: routeIdReducer,
  startEvents: startEventsReducer,
  customPlans: customPlansReducer,
  linearBuyflowName: linearBuyflowReducer,
});

type ReducerStore = ReturnType<typeof rootReducer>;

const persistWhitelist: (keyof ReducerStore)[] = [
  "surveyAnswers",
  "routeId",
  "linearBuyflowName",
];
const persistedReducer = persistReducer<ReducerStore>(
  {
    key: "root",
    storage,
    whitelist: persistWhitelist as string[],
    stateReconciler: hardSet,
  },
  rootReducer
);

const persistMiddleware = getDefaultMiddleware({
  serializableCheck: {
    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
  },
  immutableCheck: false,
});
const store = configureStore({
  reducer: persistedReducer,
  devTools: true,
  middleware: persistMiddleware,
});

/**
 * Attaches a change listener to the store.
 * @param select return state derived from store
 * @param onChange change listener callback
 */
export function observeStore<T>(
  select: (state: CoreReduxState) => T,
  onChange: (value: T) => void
) {
  let currentState = select(store.getState());

  const handleChange = () => {
    const nextState = select(store.getState());
    if (!isEqual(nextState, currentState)) {
      currentState = nextState;
      onChange(currentState);
    }
  };

  return store.subscribe(handleChange);
}

export type CoreReduxState = ReturnType<typeof store.getState>;
export type GetAppState = typeof store.getState;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
export default store;
